import { getUser } from '@dealstack/frontend/shared/dataExchange/services/auth.service';
import {
	createDeal,
	updateDeal,
} from '@dealstack/frontend/shared/dataExchange/services/project.service';
import {
	useTranslationAdminDashboard,
	useTranslationCommonForm,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { I18Namespaces } from '@dealstack/shared/lib/common/i18n/i18n.ts';
import {
	CurrencyType,
	InternalDealTag,
	SummaryDealExtendedTct,
} from '@dealstack/shared/lib/generated';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { TranslateFunction } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.types';

export enum CREATE_DEAL_FORM {
	DEAL_NAME = 'deal_name',
	TARGET_NAME = 'target_name',
	CURRENCY = 'currency',
	INTERNAL_DEAL_TAG = 'internalDealTag',
	ORGANIZATION_ID = 'organizationId',
}

export const CreateDealShape = (tCommonForm: TranslateFunction<I18Namespaces.COMMON_FORM>) =>
	z.object({
		[CREATE_DEAL_FORM.DEAL_NAME]: z
			.string({
				message: tCommonForm('field_required'),
			})
			.trim()
			.min(1, tCommonForm('field_required')),
		[CREATE_DEAL_FORM.TARGET_NAME]: z
			.string({ message: tCommonForm('field_required') })
			.trim()
			.min(1, tCommonForm('field_required')),
		[CREATE_DEAL_FORM.CURRENCY]: z.nativeEnum(CurrencyType, {
			message: tCommonForm('field_required'),
		}),
		[CREATE_DEAL_FORM.INTERNAL_DEAL_TAG]: z.nativeEnum(InternalDealTag).optional(),
		[CREATE_DEAL_FORM.ORGANIZATION_ID]: z.string().uuid().optional(),
	});

export type CreateDealFormValues = z.infer<ReturnType<typeof CreateDealShape>>;
export type DealUpdateController = ReturnType<typeof useDealUpdateController>;

export const useDealUpdateController = ({
	callback,
	isUpdate,
	deal,
}: {
	callback?: (id?: string) => unknown;
	isUpdate?: boolean;
	deal?: SummaryDealExtendedTct | null;
}) => {
	const tAdminDashboard = useTranslationAdminDashboard();
	const tCommonForm = useTranslationCommonForm();
	const createDealRequest = useMutation({
		mutationFn: (variables: Parameters<typeof createDeal>[0]) => createDeal(variables),
	});
	const updateDealRequest = useMutation({
		mutationFn: (variables: Parameters<typeof updateDeal>[0]) => updateDeal(variables),
	});
	const getUserRequest = useQuery({
		queryKey: [API_PATHS.USER_GET],
		queryFn: getUser,
		enabled: false,
		retry: false,
	});

	const createDealForm = useForm<CreateDealFormValues>({
		mode: 'all',
		reValidateMode: 'onChange',
		resolver: zodResolver(CreateDealShape(tCommonForm)),
		defaultValues: {
			[CREATE_DEAL_FORM.DEAL_NAME]: '',
			[CREATE_DEAL_FORM.TARGET_NAME]: '',
			[CREATE_DEAL_FORM.CURRENCY]: null as unknown as CurrencyType,
		},
	});
	createDealForm.watch();

	useEffect(() => {
		if (deal) {
			createDealForm.reset({
				[CREATE_DEAL_FORM.DEAL_NAME]: deal.name,
				[CREATE_DEAL_FORM.TARGET_NAME]: deal.targetName,
				[CREATE_DEAL_FORM.CURRENCY]: (deal.currency || undefined) as
					| CurrencyType
					| undefined,
				[CREATE_DEAL_FORM.INTERNAL_DEAL_TAG]: deal.internalDealTag || undefined,
				[CREATE_DEAL_FORM.ORGANIZATION_ID]: deal.organizationId || undefined,
			});
		}
	}, [deal]);

	const onSubmit = (formData: CreateDealFormValues) => {
		if (isUpdate) {
			updateDealRequest.mutate(
				{
					dealId: deal?.id || '',
					name: formData[CREATE_DEAL_FORM.DEAL_NAME],
					targetName: formData[CREATE_DEAL_FORM.TARGET_NAME],
					currency: formData[CREATE_DEAL_FORM.CURRENCY],
					internalDealTag: formData[CREATE_DEAL_FORM.INTERNAL_DEAL_TAG],
					organizationId: formData[CREATE_DEAL_FORM.ORGANIZATION_ID],
				},
				{
					onSuccess: () => {
						showToastV2({
							title: tAdminDashboard('update_deal.toast.success.title'),
							message: tAdminDashboard('update_deal.toast.success.message'),
						});
						callback?.();
					},
				}
			);
		} else {
			createDealRequest.mutate(
				{
					name: formData[CREATE_DEAL_FORM.DEAL_NAME],
					targetName: formData[CREATE_DEAL_FORM.TARGET_NAME],
					currency: formData[CREATE_DEAL_FORM.CURRENCY],
					internalDealTag: formData[CREATE_DEAL_FORM.INTERNAL_DEAL_TAG],
					organizationId: formData[CREATE_DEAL_FORM.ORGANIZATION_ID],
				},
				{
					onSuccess: async (response) => {
						await getUserRequest.refetch();
						showToastV2({
							title: tAdminDashboard('create_project.toast.success.title'),
							message: tAdminDashboard('create_project.toast.success.message'),
						});
						const dealId = response?.payload?.id;
						callback?.(dealId);
					},
				}
			);
		}
	};

	const isSettingUp = createDealRequest.isSuccess && getUserRequest.isFetching;

	return {
		createDealForm,
		createDealRequest,
		onSubmit,
		isSettingUp,
		isUpdate: !!isUpdate,
	};
};
